// Web Bluetooth ile HPRT HM-A300E gibi ESC/POS taşınabilir fiş yazıcılarına yazdırma.
// Sadece Chrome / Edge desteklenir. Localhost veya HTTPS zorunludur.

import { parseRepairPricing } from './repairPricing';

const STORAGE_KEYS = {
  enabled: 'bluetoothPrinterEnabled',
  name: 'bluetoothPrinterName',
  serviceUuid: 'bluetoothPrinterServiceUuid',
  charUuid: 'bluetoothPrinterCharUuid',
  cols: 'bluetoothPrinterCols', // 32, 42 veya 48
};

const ALLOWED_COLS = [32, 42, 48];

export const getBluetoothCols = () => {
  const v = Number(localStorage.getItem(STORAGE_KEYS.cols));
  return ALLOWED_COLS.includes(v) ? v : 32;
};

export const setBluetoothCols = (cols) => {
  if (ALLOWED_COLS.includes(Number(cols))) {
    localStorage.setItem(STORAGE_KEYS.cols, String(cols));
  }
};

// HPRT ve benzeri ESC/POS Bluetooth yazıcıların kullandığı bilinen servis UUID'leri.
const KNOWN_SERVICES = [
  '000018f0-0000-1000-8000-00805f9b34fb',
  '0000ff00-0000-1000-8000-00805f9b34fb',
  '0000ff12-0000-1000-8000-00805f9b34fb',
  '49535343-fe7d-4ae5-8fa9-9fafd205e455',
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
];

let cachedDevice = null;
let cachedCharacteristic = null;

export const isBluetoothSupported = () => {
  return typeof navigator !== 'undefined' && !!navigator.bluetooth;
};

export const getBluetoothPrinterInfo = () => ({
  enabled: localStorage.getItem(STORAGE_KEYS.enabled) === 'true',
  name: localStorage.getItem(STORAGE_KEYS.name) || '',
  serviceUuid: localStorage.getItem(STORAGE_KEYS.serviceUuid) || '',
  charUuid: localStorage.getItem(STORAGE_KEYS.charUuid) || '',
});

export const setBluetoothPrinterEnabled = (enabled) => {
  localStorage.setItem(STORAGE_KEYS.enabled, enabled ? 'true' : 'false');
};

export const clearBluetoothPrinter = () => {
  localStorage.removeItem(STORAGE_KEYS.enabled);
  localStorage.removeItem(STORAGE_KEYS.name);
  localStorage.removeItem(STORAGE_KEYS.serviceUuid);
  localStorage.removeItem(STORAGE_KEYS.charUuid);
  if (cachedDevice && cachedDevice.gatt && cachedDevice.gatt.connected) {
    try { cachedDevice.gatt.disconnect(); } catch {}
  }
  cachedDevice = null;
  cachedCharacteristic = null;
};

export const pairBluetoothPrinter = async () => {
  if (!isBluetoothSupported()) {
    throw new Error('Bu tarayıcı Web Bluetooth desteklemiyor. Chrome veya Edge kullanın.');
  }

  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: KNOWN_SERVICES,
  });

  const server = await device.gatt.connect();
  const services = await server.getPrimaryServices();

  let foundService = null;
  let foundChar = null;
  for (const service of services) {
    try {
      const chars = await service.getCharacteristics();
      for (const ch of chars) {
        if (ch.properties.write || ch.properties.writeWithoutResponse) {
          foundService = service;
          foundChar = ch;
          break;
        }
      }
    } catch {}
    if (foundChar) break;
  }

  if (!foundChar) {
    try { device.gatt.disconnect(); } catch {}
    throw new Error('Cihazda yazılabilir karakteristik bulunamadı. Doğru yazıcıyı seçtiğinizden emin olun.');
  }

  localStorage.setItem(STORAGE_KEYS.name, device.name || 'Bluetooth Yazıcı');
  localStorage.setItem(STORAGE_KEYS.serviceUuid, foundService.uuid);
  localStorage.setItem(STORAGE_KEYS.charUuid, foundChar.uuid);
  localStorage.setItem(STORAGE_KEYS.enabled, 'true');

  cachedDevice = device;
  cachedCharacteristic = foundChar;

  return { name: device.name || 'Bluetooth Yazıcı', serviceUuid: foundService.uuid, charUuid: foundChar.uuid };
};

const ensureCharacteristic = async () => {
  if (cachedCharacteristic && cachedDevice && cachedDevice.gatt && cachedDevice.gatt.connected) {
    return cachedCharacteristic;
  }

  const info = getBluetoothPrinterInfo();
  if (!info.serviceUuid || !info.charUuid) {
    throw new Error('Bluetooth yazıcı eşleştirilmemiş. Ayarlardan eşleştirin.');
  }

  if (navigator.bluetooth.getDevices) {
    try {
      const devices = await navigator.bluetooth.getDevices();
      const dev = devices.find(d => d.name === info.name);
      if (dev) {
        const server = await dev.gatt.connect();
        const service = await server.getPrimaryService(info.serviceUuid);
        const ch = await service.getCharacteristic(info.charUuid);
        cachedDevice = dev;
        cachedCharacteristic = ch;
        return ch;
      }
    } catch (e) {
      console.warn('BT yeniden bağlanma başarısız:', e);
    }
  }

  throw new Error('Bluetooth yazıcı bağlantısı kayboldu. Lütfen Ayarlar\'dan tekrar Eşleştir\'e basın.');
};

const writeChunks = async (characteristic, bytes) => {
  const CHUNK = 100;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.slice(i, i + CHUNK);
    if (characteristic.properties.writeWithoutResponse) {
      await characteristic.writeValueWithoutResponse(slice);
    } else {
      await characteristic.writeValue(slice);
    }
    await new Promise(r => setTimeout(r, 25));
  }
};

export const printRawToBluetooth = async (bytes) => {
  const ch = await ensureCharacteristic();
  await writeChunks(ch, bytes);
};

// ---------- ESC/POS yardımcıları ----------

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

const TR_MAP = {
  'ı': 'i', 'İ': 'I', 'ş': 's', 'Ş': 'S', 'ğ': 'g', 'Ğ': 'G',
  'ü': 'u', 'Ü': 'U', 'ö': 'o', 'Ö': 'O', 'ç': 'c', 'Ç': 'C',
  '₺': 'TL', '€': 'EUR', '–': '-', '—': '-', '“': '"', '”': '"', '‘': "'", '’': "'",
};
const turkishToAscii = (s) => String(s).replace(/[ıİşŞğĞüÜöÖçÇ₺€–—“”‘’]/g, ch => TR_MAP[ch] || ch);

// Karakter genişliği (yazıcı modeline ve fonta göre değişir).
// 32 = HPRT 58mm Font A, 42 = 58mm Font B, 48 = 80mm Font A.
// Print fonksiyonları başında refreshCols() çağrılır, ayardan okunur.
let COLS = 32;
const refreshCols = () => { COLS = getBluetoothCols(); };

class EscPosBuilder {
  constructor() { this.bytes = []; }
  push(...arr) { for (const a of arr) this.bytes.push(a); return this; }
  init() { return this.push(ESC, 0x40); }
  align(mode) { return this.push(ESC, 0x61, mode); } // 0=left 1=center 2=right
  bold(on) { return this.push(ESC, 0x45, on ? 1 : 0); }
  size(width, height) {
    const n = ((Math.max(0, Math.min(7, width))) << 4) | (Math.max(0, Math.min(7, height)));
    return this.push(GS, 0x21, n);
  }
  text(s) {
    const ascii = turkishToAscii(s);
    for (let i = 0; i < ascii.length; i++) this.bytes.push(ascii.charCodeAt(i) & 0xff);
    return this;
  }
  line(s = '') { return this.text(s).push(LF); }
  feed(n = 1) { for (let i = 0; i < n; i++) this.bytes.push(LF); return this; }
  divider(char = '-', width = COLS) { return this.line(char.repeat(width)); }
  cut() { return this.push(GS, 0x56, 0x00); }

  // CODE128 barkod. data ASCII bekler. height=dot, modulWidth=2-6.
  barcode128(data, { height = 60, modulWidth = 2, hriPosition = 0 } = {}) {
    const ascii = turkishToAscii(String(data));
    // GS H n  (HRI position: 0=yok, 1=üst, 2=alt, 3=her ikisi)
    this.push(GS, 0x48, hriPosition);
    // GS h n  (yükseklik)
    this.push(GS, 0x68, Math.max(1, Math.min(255, height)));
    // GS w n  (modul genişliği)
    this.push(GS, 0x77, Math.max(2, Math.min(6, modulWidth)));
    // GS k 73 n {B + data  → Function B (CODE128, n = veri uzunluğu)
    const payload = [0x7B, 0x42]; // {B = CODE128 alt mode B
    for (let i = 0; i < ascii.length; i++) payload.push(ascii.charCodeAt(i) & 0xff);
    this.push(GS, 0x6B, 0x49, payload.length);
    for (const byte of payload) this.bytes.push(byte);
    this.push(LF);
    return this;
  }

  // QR kod. text URL/metin. size 1-16 (modul boyutu).
  qrcode(text, { size = 6, errorCorrection = 49 } = {}) {
    const data = String(text);
    const dataBytes = [];
    for (let i = 0; i < data.length; i++) {
      const code = data.charCodeAt(i);
      if (code < 256) dataBytes.push(code);
      else dataBytes.push(0x3F); // '?'
    }
    // Model: GS ( k 04 00 31 41 32 00
    this.push(GS, 0x28, 0x6B, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00);
    // Size: GS ( k 03 00 31 43 n
    this.push(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x43, Math.max(1, Math.min(16, size)));
    // Error correction: GS ( k 03 00 31 45 n  (48=L, 49=M, 50=Q, 51=H)
    this.push(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x45, errorCorrection);
    // Store data: GS ( k pL pH 31 50 30 d1...dk
    const len = dataBytes.length + 3;
    this.push(GS, 0x28, 0x6B, len & 0xFF, (len >> 8) & 0xFF, 0x31, 0x50, 0x30);
    for (const byte of dataBytes) this.bytes.push(byte);
    // Print: GS ( k 03 00 31 51 30
    this.push(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x51, 0x30);
    return this;
  }

  toBytes() { return new Uint8Array(this.bytes); }
}

// Uzun metni kelime kelime sarar (word-wrap), her satır max width karakter.
const wrapText = (text, width = COLS) => {
  const ascii = turkishToAscii(String(text || ''));
  const words = ascii.split(/\s+/);
  const lines = [];
  let current = '';
  for (const w of words) {
    if (!w) continue;
    if (w.length > width) {
      // çok uzun kelimeyi parçala
      if (current) { lines.push(current); current = ''; }
      for (let i = 0; i < w.length; i += width) {
        lines.push(w.slice(i, i + width));
      }
      continue;
    }
    if ((current + (current ? ' ' : '') + w).length > width) {
      lines.push(current);
      current = w;
    } else {
      current = current ? current + ' ' + w : w;
    }
  }
  if (current) lines.push(current);
  return lines;
};

const padRight = (s, w) => {
  s = String(s);
  if (s.length >= w) return s.slice(0, w);
  return s + ' '.repeat(w - s.length);
};

// Sol-sağ iki sütun: sol etiket, sağ değer.
const twoCols = (left, right, total = COLS) => {
  const l = turkishToAscii(left);
  const r = turkishToAscii(right);
  const leftMax = total - r.length - 1;
  const ll = l.length > leftMax ? l.slice(0, Math.max(0, leftMax)) : l;
  return padRight(ll, total - r.length) + r;
};

// HTML şablonundaki kasiyer maskeleme: "Ahmet Yılmaz" → "A*** Y***"
const maskStaffName = (name) => {
  if (!name) return '';
  return String(name).split(' ').filter(Boolean).map(w => w.charAt(0).toUpperCase() + '***').join(' ');
};

// Ürün bazlı fiyatlandırma: ürün satırının altına ücret (bkz. utils/repairPricing.js)
const printItemPrice = (b, repair, it) => {
  if (parseRepairPricing(repair)?.mode !== 'item' || !it.price_type) return;
  let label;
  if (it.price_type === 'free') label = 'UCRETSIZ';
  else if (it.price_type === 'pending') label = 'HESAPLANACAK';
  else {
    const finalPrice = it.final_price != null ? Number(it.final_price) : Number(it.price);
    label = `${finalPrice.toLocaleString('tr-TR')} TL`;
    if (finalPrice < Number(it.price)) label = `(${Number(it.price).toLocaleString('tr-TR')}) ${label}`;
  }
  b.line(twoCols('   Ucret:', label));
};

// İndirim varsa Ara Toplam / İndirim satırları
const printPricingSummary = (b, repair) => {
  const pr = parseRepairPricing(repair);
  const itemDisc = Number(pr?.item_discount_total) || 0;
  const basketDisc = Number(pr?.discount_amount) || 0;
  if (!(itemDisc + basketDisc > 0)) return;
  b.line(twoCols('Ara Toplam:', `${Number(pr.subtotal).toLocaleString('tr-TR')} TL`));
  if (itemDisc > 0) b.line(twoCols('Urun Indirimleri:', `-${itemDisc.toLocaleString('tr-TR')} TL`));
  if (basketDisc > 0) {
    const pct = pr.discount_type === 'percent' ? ` (%${Number(pr.discount_value).toLocaleString('tr-TR')})` : '';
    b.line(twoCols(`${pr.mode === 'item' ? 'Sepet' : 'Fis'} Indirimi${pct}:`, `-${basketDisc.toLocaleString('tr-TR')} TL`));
  }
};

const parseRepairItems = (repair) => {
  let items = [];
  if (repair.items) {
    try { items = typeof repair.items === 'string' ? JSON.parse(repair.items) : repair.items; } catch {}
  }
  if (!items || items.length === 0) {
    items = [{
      item_type: repair.item_type,
      item_description: repair.item_description,
      problem_description: repair.problem_description,
    }];
  }
  return items.filter(i => i.item_description && String(i.item_description).trim());
};

// Etiket + değer satırı: sığarsa yan yana (twoCols), sığmazsa label tek satır + value sağa hizalı altta.
const printRow = (b, label, value, { bold = false } = {}) => {
  const l = turkishToAscii(label);
  const v = turkishToAscii((value === undefined || value === null || value === '') ? '-' : String(value));
  if (bold) b.bold(true);
  if (l.length + 1 + v.length <= COLS) {
    b.line(twoCols(l, v));
  } else {
    b.line(l);
    wrapText(v, COLS).forEach(line => b.line(twoCols('', line)));
  }
  if (bold) b.bold(false);
};

// Mağaza başlığını ESC/POS olarak basar.
const printStoreHeader = (b, storeInfo) => {
  b.align(1).bold(true).size(1, 1);
  b.line(storeInfo?.name || 'AKIK');
  b.size(0, 0);
  if (storeInfo?.branch) b.line(storeInfo.branch);
  b.bold(false);
  if (storeInfo?.address) {
    wrapText(storeInfo.address, COLS).forEach(l => b.line(l));
  }
  if (storeInfo?.phone) b.line(storeInfo.phone);
  if (storeInfo?.web) b.line(storeInfo.web);
};

// Kapora ödeme detaylarını parse et (JSON string ya da array gelebilir)
const parseDepositPaymentDetails = (repair) => {
  let details = repair?.deposit_payment_details;
  if (typeof details === 'string') {
    try { details = JSON.parse(details); } catch (e) { details = null; }
  }
  return Array.isArray(details) ? details : [];
};

// Tek bir ödeme kaleminin okunabilir tip etiketi: "Nakit", "Kredi Kartı", "Havale Sahis - X Bank"
const formatPaymentTypeLabel = (p) => {
  if (!p || !p.method) return '';
  if (p.method === 'Havale/EFT' || p.method === 'Havale') {
    return `Havale${p.bank_account ? ' - ' + p.bank_account : ''}`;
  }
  return p.method;
};

// Kapora ödeme tipini fişe basar (line/twoCols Türkçe→ASCII çevirir)
const printDepositMethods = (b, repair) => {
  const details = parseDepositPaymentDetails(repair);
  if (details.length === 0) {
    if (repair?.deposit_payment_method) {
      b.line(twoCols('Odeme Tipi:', repair.deposit_payment_method));
    }
    return;
  }
  if (details.length === 1) {
    b.line(twoCols('Odeme Tipi:', formatPaymentTypeLabel(details[0])));
    return;
  }
  details.forEach(p => {
    b.line(twoCols(`${formatPaymentTypeLabel(p)}:`, `${Number(p.amount).toLocaleString('tr-TR')} TL`));
  });
};

// MÜŞTERİ TESLİM FİŞİ
export const buildRepairCustomerReceiptBytes = ({ repair, storeInfo }) => {
  refreshCols();
  const b = new EscPosBuilder();
  const repairCode = repair.repair_code || String(repair.id).padStart(6, '0');
  const createdAt = new Date(repair.created_at).toLocaleDateString('tr-TR', {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
  const estimatedDelivery = repair.estimated_delivery
    ? new Date(repair.estimated_delivery).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })
    : '-';
  const deadline = repair.deadline
    ? new Date(repair.deadline).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })
    : '-';
  const items = parseRepairItems(repair);

  b.init();

  // Mağaza başlığı
  printStoreHeader(b, storeInfo);
  b.feed(1);

  // Alt başlık
  b.align(1).bold(true).line('Tamirat Fisi').bold(false);
  b.divider('-');

  // Fiş no kutusu (büyük) + barkod
  b.align(1).line('FIS NO');
  b.size(2, 2).bold(true).line(repairCode).bold(false).size(0, 0);
  b.barcode128(repairCode, { height: 50, modulWidth: 2 });
  b.divider('-');

  // Müşteri bilgisi
  b.align(0);
  b.bold(true).line(repair.customer_name || '-').bold(false);
  b.line(repair.customer_phone || '-');
  b.line(`Teslim Kodu: ${repairCode}`);
  b.divider('-');

  // Ürünler
  b.bold(true).line(`URUNLER (${items.length} adet)`).bold(false);
  items.forEach((it, i) => {
    const head = `${i + 1}. ${it.item_description}`;
    wrapText(head, COLS).forEach(l => b.line(l));
    if (it.problem_description) {
      wrapText(`   - ${it.problem_description}`, COLS).forEach(l => b.line(l));
    }    printItemPrice(b, repair, it);
  });
  b.divider('-');

  // Tarih bilgileri
  b.bold(true).line('TARIH BILGILERI').bold(false);
  printRow(b, 'Teslim Alma:', createdAt);
  printRow(b, 'Tahmini Teslim:', estimatedDelivery);
  printRow(b, 'En Gec Teslim:', deadline);
  if (repair.staff_name) {
    printRow(b, 'Kasiyer:', maskStaffName(repair.staff_name));
  }
  b.divider('-');

  // Ücret bilgileri
  if (repair.price_type === 'free') {
    b.bold(true).line('UCRET BILGILERI').bold(false);
    b.line(twoCols('Ucret:', 'UCRETSIZ'));
    b.divider('-');
  } else if (repair.price_type === 'pending') {
    b.bold(true).line('UCRET BILGILERI').bold(false);
    b.line(twoCols('Ucret:', 'HESAPLANACAK'));
    b.divider('-');
  } else if (Number(repair.estimated_price) > 0) {
    b.bold(true).line('UCRET BILGILERI').bold(false);
    printPricingSummary(b, repair);
    b.line(twoCols('Ucret:', `${Number(repair.estimated_price).toLocaleString('tr-TR')} TL`));
    if (Number(repair.deposit_amount) > 0) {
      b.line(twoCols('Alinan Ucret:', `${Number(repair.deposit_amount).toLocaleString('tr-TR')} TL`));
      printDepositMethods(b, repair);
      const kalan = Number(repair.estimated_price) - Number(repair.deposit_amount);
      b.divider('.');
      b.bold(true).line(twoCols('Kalan Tutar:', `${kalan.toLocaleString('tr-TR')} TL`)).bold(false);
    }
    b.divider('-');
  }

  // Notlar
  if (repair.notes) {
    b.bold(true).line('NOTLAR').bold(false);
    wrapText(repair.notes, COLS).forEach(l => b.line(l));
    b.divider('-');
  }

  // Tamirat takip QR kodu
  b.align(1);
  b.qrcode(`https://akik.qubite.net/tamirat?code=${repairCode}`, { size: 6 });
  b.line('Tamirat takibi icin QR okutunuz');
  b.divider('-');

  // Footer
  b.align(1);
  b.bold(true).line('TESEKKUR EDERIZ YINE BEKLERIZ').bold(false);
  b.line('BILGI FISIDIR MALI DEGERI YOKTUR');
  b.feed(1);
  wrapText('15 GUN ICERISINDE TESLIM ALINMAYAN URUNLERDEN FIRMAMIZ SORUMLU DEGILDIR', COLS).forEach(l => b.line(l));
  b.feed(1);
  wrapText('URUNLERINIZI YALNIZCA BU FIS ILE TESLIM ALABILIRSINIZ FISINIZI SAKLAYINIZ', COLS).forEach(l => b.line(l));

  b.feed(4);
  b.cut();
  return b.toBytes();
};

// İÇ TAMİRAT FİŞİ
export const buildRepairInternalReceiptBytes = ({ repair, storeInfo }) => {
  refreshCols();
  const b = new EscPosBuilder();
  const repairCode = repair.repair_code || String(repair.id).padStart(6, '0');
  const createdAt = new Date(repair.created_at).toLocaleDateString('tr-TR', {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
  const estimatedDelivery = repair.estimated_delivery
    ? new Date(repair.estimated_delivery).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })
    : '-';
  const deadline = repair.deadline
    ? new Date(repair.deadline).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })
    : '-';
  const items = parseRepairItems(repair);

  b.init();

  // Mağaza başlığı
  printStoreHeader(b, storeInfo);
  b.feed(1);

  // Başlık (büyük)
  b.align(1).bold(true).size(1, 1).line('TAMIRAT FISI').size(0, 0).bold(false);
  b.divider('=');

  // Fiş no + barkod
  b.align(1).line('FIS NO');
  b.size(2, 2).bold(true).line(repairCode).bold(false).size(0, 0);
  b.barcode128(repairCode, { height: 50, modulWidth: 2 });
  b.divider('-');

  // Müşteri özet (sol hizalı)
  b.align(0);
  b.bold(true).line(repair.customer_name || '-').bold(false);
  b.line(repair.customer_phone || '-');
  printRow(b, 'Tarih:', createdAt);
  if (repair.staff_name) {
    printRow(b, 'Kasiyer:', maskStaffName(repair.staff_name));
  }
  b.divider('-');

  // Ürünler
  b.bold(true).line(`URUNLER (${items.length} adet)`).bold(false);
  items.forEach((it, i) => {
    const head = `${i + 1}. ${it.item_description}`;
    wrapText(head, COLS).forEach(l => b.line(l));
    if (it.problem_description) {
      wrapText(`   - ${it.problem_description}`, COLS).forEach(l => b.line(l));
    }    printItemPrice(b, repair, it);
  });
  b.divider('-');

  // Tarih bilgileri
  b.bold(true).line('TARIH BILGILERI').bold(false);
  printRow(b, 'Teslim Alma:', createdAt);
  printRow(b, 'Tahmini Teslim:', estimatedDelivery);
  printRow(b, 'En Gec Teslim:', deadline);
  b.divider('-');

  // Ücret
  if (repair.price_type === 'free') {
    b.bold(true).line('UCRET BILGILERI').bold(false);
    b.line(twoCols('Ucret:', 'UCRETSIZ'));
    b.divider('-');
  } else if (repair.price_type === 'pending') {
    b.bold(true).line('UCRET BILGILERI').bold(false);
    b.line(twoCols('Ucret:', 'HESAPLANACAK'));
    b.divider('-');
  } else if (Number(repair.estimated_price) > 0 || Number(repair.deposit_amount) > 0) {
    b.bold(true).line('UCRET BILGILERI').bold(false);
    if (Number(repair.estimated_price) > 0) {
      printPricingSummary(b, repair);
      b.line(twoCols('Ucret:', `${Number(repair.estimated_price).toLocaleString('tr-TR')} TL`));
    }
    if (Number(repair.deposit_amount) > 0) {
      b.line(twoCols('Alinan Ucret:', `${Number(repair.deposit_amount).toLocaleString('tr-TR')} TL`));
      printDepositMethods(b, repair);
    }
    if (Number(repair.estimated_price) > 0 && Number(repair.deposit_amount) > 0) {
      const kalan = Number(repair.estimated_price) - Number(repair.deposit_amount);
      b.divider('.');
      b.bold(true).line(twoCols('Kalan Tutar:', `${kalan.toLocaleString('tr-TR')} TL`)).bold(false);
    }
    b.divider('-');
  }

  // Notlar
  if (repair.notes) {
    b.bold(true).line('NOTLAR').bold(false);
    wrapText(repair.notes, COLS).forEach(l => b.line(l));
    b.divider('-');
  }

  // Footer
  b.align(1);
  b.line('Bu fis tamirat takibi icin kullanilir.');
  b.feed(1);
  b.line(`Yazdirma: ${new Date().toLocaleString('tr-TR')}`);

  b.feed(4);
  b.cut();
  return b.toBytes();
};

// Test fişi
export const buildTestReceiptBytes = () => {
  refreshCols();
  const b = new EscPosBuilder();
  b.init();
  b.align(1).bold(true).size(1, 1).line('AKIK BT TEST').size(0, 0).bold(false);
  b.divider('=');
  b.align(0);
  b.line('Bluetooth yazici testi');
  b.line(`Tarih: ${new Date().toLocaleString('tr-TR')}`);
  b.divider();
  b.line('Turkce: ' + turkishToAscii('iIşŞğĞüÜöÖçÇ ₺'));
  b.divider();
  b.align(1).line('Test basarili').feed(4).cut();
  return b.toBytes();
};
